import React, { useEffect, useMemo, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import FormSkel from "./FormSkel";

const actions = [
  { value: "add", label: "Dodaj zestaw reklam do terminali" },
  { value: "replace", label: "Zamień obecne reklamy na wybrany zestaw" },
  { value: "remove", label: "Usuń wybrany zestaw reklam z terminali" },
  { value: "clear", label: "Wyczyść wszystkie reklamy z terminali" },
];

const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
};

export default ({
  selectedTerminals = [],
  advertises = [],
  screenData,
  handleSubmit,
  handleClose,
  handleTerminalToggle,
  isSaving = false,
}) => {
  const [mode, setMode] = useState("replace");
  const [advertiseId, setAdvertiseId] = useState("");
  const [advertisesLoaded, setAdvertisesLoaded] = useState(false);
  const firstAdvertisesRender = useRef(true);

  useEffect(() => {
    if (firstAdvertisesRender.current) {
      firstAdvertisesRender.current = false;
      return;
    }

    setAdvertisesLoaded(true);
  }, [advertises]);

  const availableAdvertises = useMemo(() => {
    return (advertises || [])
      .filter((advertise) => {
        const isActive = advertise?.IsActive === undefined ? true : advertise.IsActive;
        const filesCount = Number(advertise?.FilesCount || 0);
        const ratio = String(advertise?.ScreenRatioKey || "").trim();

        return (
          isActive &&
          filesCount > 0 &&
          (!screenData?.key || !ratio || ratio === screenData.key)
        );
      })
      .sort((a, b) =>
        String(a?.Name || "").localeCompare(String(b?.Name || ""), "pl", {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [advertises, screenData?.key]);

  const selectedAdvertise = useMemo(() => {
    const id = Number(advertiseId);
    return availableAdvertises.find((advertise) => Number(advertise?.AdvertiseId) === id) || null;
  }, [availableAdvertises, advertiseId]);

  const getCurrentAdvertises = (terminal) => {
    const terminalId = Number(terminal?.TerminalId);

    const names = (advertises || [])
      .filter((advertise) =>
        parseArray(advertise?.TerminalIds)
          .map(Number)
          .includes(terminalId)
      )
      .map((advertise) => String(advertise?.Name || "").trim())
      .filter(Boolean);

    return names.length ? names.join(", ") : "Brak reklam";
  };

  const onSubmit = (evt) => {
    evt.preventDefault();

    if (!selectedTerminals.length) {
      NotificationManager.warning("Wybierz co najmniej jeden terminal");
      return;
    }

    if (mode !== "clear" && !selectedAdvertise) {
      NotificationManager.warning("Wybierz zestaw reklam");
      return;
    }

    handleSubmit({
      mode,
      advertiseId: mode === "clear" ? null : Number(selectedAdvertise.AdvertiseId),
    });
  };

  return (
    <FormSkel headerText="Edytuj reklamy na terminalach" handleClose={handleClose}>
      <form onSubmit={onSubmit} id="modal-form" autoComplete="off">
        <div className="form-group">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <div className="h6 mb-0 selected-terminals-alignment pe-none">Wybrane terminale: </div>
            <div className="badge badge-primary align-middle m-1 pe-none resolution-alignment" style={{ fontSize: "11px" }}>
              Rozdzielczość: {screenData?.resolution || "-"}
            </div>
          </div>

          <div className="border rounded p-2 mb-3 custom-scroll" style={{ maxHeight: 180, overflowY: "auto" }}>
            <div className="d-flex justify-content-between px-2 mb-1 pe-none" style={{ fontSize: "15px" }}>
              <div className="font-weight-bold">Terminal</div>
              <div className="font-weight-bold">Obecny zestaw reklam</div>
            </div>

            {selectedTerminals.map((terminal) => {
              const currentAdvertises = getCurrentAdvertises(terminal);

              return (
                <label
                  key={terminal.TerminalId}
                  className="d-flex align-items-center justify-content-between selected-terminals-input"
                >
                  <span className="badge">
                    <input
                      type="checkbox"
                      className="mr-2 badge"
                      checked
                      onChange={() => handleTerminalToggle(terminal)}
                    />
                    <span>
                      {[terminal.TerminalTypeName, terminal.Model, terminal.SerialNo]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                  </span>

                  <span
                    className="badge ml-2 text-right"
                    title={currentAdvertises}
                    style={{
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {currentAdvertises}
                  </span>
                </label>
              );
            })}
          </div>

          <label className="h6 primary align-middle m-1 action-alignment">Akcja</label>
          <div className="border rounded p-2 mb-3 custom-scroll">
            {actions.map((action) => (
              <div className="form-check" key={action.value}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="mode"
                  id={`terminalAdvertiseMode-${action.value}`}
                  value={action.value}
                  checked={mode === action.value}
                  onChange={(evt) => setMode(evt.target.value)}
                />
                <b>
                  <label className="form-check-label" htmlFor={`terminalAdvertiseMode-${action.value}`}>
                    <div className="action-label-alignment">{action.label}</div>
                  </label>
                </b>
              </div>
            ))}
          </div>

          {mode !== "clear" && (
            <>
              <label className="h6 primary align-middle m-1 ad-set-alignment">Zestaw reklam</label>
              <div className="advertises-select-wrapper">
                <select
                  className="mt-2 form-control advertises-select form-control-sm mr-1"
                  value={advertiseId}
                  onChange={(evt) => setAdvertiseId(evt.target.value)}
                  disabled={!advertisesLoaded || isSaving}
                  style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
                >
                  <option value="">Wybierz zestaw reklam</option>
                  {availableAdvertises.map((advertise) => (
                    <option value={advertise.AdvertiseId} key={advertise.AdvertiseId}>
                      {advertise.Name}
                    </option>
                  ))}
                </select>

                <i className="fas fa-chevron-down advertises-select-chevron" />
              </div>
            </>
          )}

          {mode !== "clear" && selectedAdvertise && (
            <div className="border rounded mt-3 p-2" style={{ fontSize: "14px" }}>
              <b>
                <div>Nazwa: {selectedAdvertise.Name}</div>
                <div>Typ: {selectedAdvertise.PackageType || "-"}</div>
                <div>Ekran: {selectedAdvertise.ScreenRatioKey || "-"}</div>
                <div>Pliki: {selectedAdvertise.FilesCount || 0}</div>
                <div>Przypisane terminale: {selectedAdvertise.TerminalsCount || 0}</div>
              </b>
            </div>
          )}

          {mode !== "clear" && advertisesLoaded && !availableAdvertises.length && (
            <div className="alert alert-warning mt-3 mb-0 pe-none" style={{ fontSize: "14px" }}>
              <b>Brak aktywnych zestawów reklam zawierających pliki dla wybranej rozdzielczości.</b>
            </div>
          )}
        </div>
      </form>
    </FormSkel>
  );
};